import { useCallback, useEffect, useRef, useState } from 'react'
import { toast } from 'react-toastify'
import {
  CloudArrowUpIcon,
  MagnifyingGlassIcon,
  PencilSquareIcon,
  PlayCircleIcon,
  TrashIcon,
  EyeIcon,
  EyeSlashIcon,
  XMarkIcon,
} from '@heroicons/react/24/outline'
import {
  AdminApi,
  AdminVideo,
  AdminVideoUsage,
  AdminVideoUploadProgress,
  ADMIN_VIDEO_MAX_BYTES,
} from '../../services/adminApi'
import { formatDbDateTimeVi } from '../../utils/dbDateDisplay'
import SecureVideo from '../../components/learning/SecureVideo'
import { useSecurePlaybackStream } from '../../hooks/useSecurePlaybackStream'

const formatBytes = (bytes?: number | null) => {
  if (bytes == null || Number.isNaN(bytes)) return '—'
  if (bytes < 1024) return `${bytes} B`
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`
  if (bytes < 1024 * 1024 * 1024) return `${(bytes / (1024 * 1024)).toFixed(1)} MB`
  return `${(bytes / (1024 * 1024 * 1024)).toFixed(2)} GB`
}

const formatDuration = (sec?: number | null) => {
  if (sec == null || Number.isNaN(sec) || sec <= 0) return '—'
  const total = Math.round(sec)
  const h = Math.floor(total / 3600)
  const m = Math.floor((total % 3600) / 60)
  const s = total % 60
  if (h > 0) return `${h}:${String(m).padStart(2, '0')}:${String(s).padStart(2, '0')}`
  return `${m}:${String(s).padStart(2, '0')}`
}

const AdminVideoPreviewModal = ({
  video,
  onClose,
}: {
  video: AdminVideo
  onClose: () => void
}) => {
  const { streamUrl, format, error, videoRef, applyResumeOnMetadata, recoverFromStreamError } =
    useSecurePlaybackStream('video', video.id)

  useEffect(() => {
    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose()
    }
    window.addEventListener('keydown', onKeyDown)
    return () => window.removeEventListener('keydown', onKeyDown)
  }, [onClose])

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/70 p-4"
      onClick={onClose}
    >
      <div
        className="w-full max-w-4xl overflow-hidden rounded-lg bg-m2-surface-container shadow-xl"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-start justify-between gap-3 border-b border-m2-outline-variant px-4 py-3">
          <div className="min-w-0">
            <h2 className="truncate text-lg font-semibold text-m2-on-surface">{video.title}</h2>
            <p className="text-xs text-m2-on-surface-variant">
              #{video.id}
              {video.duration != null ? ` · ${formatDuration(video.duration)}` : ''}
              {video.fileName ? ` · ${video.fileName}` : ''}
            </p>
          </div>
          <button
            type="button"
            title="Đóng"
            onClick={onClose}
            className="rounded p-1.5 text-m2-on-surface-variant hover:bg-m2-surface-high hover:text-m2-on-surface"
          >
            <XMarkIcon className="h-5 w-5" />
          </button>
        </div>

        <div className="bg-black">
          {error ? (
            <div className="px-4 py-12 text-center text-sm text-red-200">{error}</div>
          ) : streamUrl ? (
            <SecureVideo
              key={video.id}
              format={format}
              src={streamUrl}
              videoRef={videoRef}
              controls
              autoPlay
              playsInline
              onError={() => recoverFromStreamError()}
              onLoadedMetadata={(event) => {
                applyResumeOnMetadata(event.currentTarget)
              }}
              className="max-h-[70vh] w-full bg-black object-contain"
            />
          ) : (
            <div className="px-4 py-12 text-center text-sm text-m2-on-surface-variant">
              Đang tải video...
            </div>
          )}
        </div>
      </div>
    </div>
  )
}

const AdminVideosPage = () => {
  const [loading, setLoading] = useState(false)
  const [items, setItems] = useState<AdminVideo[]>([])
  const [page, setPage] = useState(1)
  const [totalPages, setTotalPages] = useState(1)
  const [total, setTotal] = useState(0)
  const [q, setQ] = useState('')
  const [qInput, setQInput] = useState('')
  const [includeInactive, setIncludeInactive] = useState(false)

  const [uploading, setUploading] = useState(false)
  const [uploadProgress, setUploadProgress] = useState<AdminVideoUploadProgress | null>(null)
  const [uploadTitle, setUploadTitle] = useState('')
  const abortRef = useRef<AbortController | null>(null)
  const fileInputRef = useRef<HTMLInputElement>(null)

  const [editing, setEditing] = useState<AdminVideo | null>(null)
  const [previewing, setPreviewing] = useState<AdminVideo | null>(null)
  const [deleting, setDeleting] = useState<AdminVideo | null>(null)
  const [deleteUsages, setDeleteUsages] = useState<AdminVideoUsage[]>([])
  const [loadingUsages, setLoadingUsages] = useState(false)
  const [removingContentId, setRemovingContentId] = useState<number | null>(null)
  const [confirmingHardDelete, setConfirmingHardDelete] = useState(false)
  const [editTitle, setEditTitle] = useState('')
  const [editDescription, setEditDescription] = useState('')
  const [editDuration, setEditDuration] = useState('')
  const [savingEdit, setSavingEdit] = useState(false)
  const [actingId, setActingId] = useState<number | null>(null)

  const loadData = useCallback(
    async (overrides?: { page?: number; q?: string; includeInactive?: boolean }) => {
      const nextPage = overrides?.page ?? page
      const nextQ = overrides?.q ?? q
      const nextInactive = overrides?.includeInactive ?? includeInactive
      setLoading(true)
      try {
        const res = await AdminApi.listVideos({
          page: nextPage,
          limit: 20,
          q: nextQ || undefined,
          includeInactive: nextInactive,
        })
        setItems(res.data.items)
        setTotal(res.data.pagination.total)
        setTotalPages(res.data.pagination.totalPages)
      } catch {
        toast.error('Không tải được danh sách video')
      } finally {
        setLoading(false)
      }
    },
    [page, q, includeInactive]
  )

  useEffect(() => {
    void loadData({ page })
  }, [page, q, includeInactive]) // eslint-disable-line react-hooks/exhaustive-deps

  const applySearch = () => {
    setQ(qInput.trim())
    setPage(1)
  }

  const openEdit = (video: AdminVideo) => {
    setEditing(video)
    setEditTitle(video.title || '')
    setEditDescription(video.description || '')
    setEditDuration(video.duration != null ? String(video.duration) : '')
  }

  const handleSaveEdit = async () => {
    if (!editing) return
    if (!editTitle.trim()) {
      toast.error('Tiêu đề không được để trống')
      return
    }
    setSavingEdit(true)
    try {
      const res = await AdminApi.updateVideo(editing.id, {
        title: editTitle.trim(),
        description: editDescription.trim() || null,
        duration: editDuration === '' ? undefined : Number(editDuration) || 0,
      })
      if (res.success) {
        toast.success(res.message || 'Đã cập nhật video')
        setEditing(null)
        void loadData()
      } else {
        toast.error(res.message || 'Cập nhật thất bại')
      }
    } catch (err: any) {
      toast.error(err?.response?.data?.message || 'Không cập nhật được video')
    } finally {
      setSavingEdit(false)
    }
  }

  const handleToggleActive = async (video: AdminVideo) => {
    const nextActive = video.isActive === 0 ? 1 : 0
    const label = nextActive ? 'hiện lại' : 'ẩn'
    if (!window.confirm(`Bạn muốn ${label} video "${video.title}"?`)) return
    setActingId(video.id)
    try {
      const res = await AdminApi.updateVideo(video.id, { isActive: nextActive })
      if (res.success) {
        toast.success(nextActive ? 'Đã hiện video' : 'Đã ẩn video')
        void loadData()
      } else {
        toast.error(res.message || 'Thao tác thất bại')
      }
    } catch (err: any) {
      toast.error(err?.response?.data?.message || 'Thao tác thất bại')
    } finally {
      setActingId(null)
    }
  }

  const openDeleteModal = async (video: AdminVideo) => {
    setDeleting(video)
    setDeleteUsages([])
    setConfirmingHardDelete(false)
    setLoadingUsages(true)
    try {
      const res = await AdminApi.getVideoUsages(video.id)
      if (res.success) {
        setDeleteUsages(res.data.usages || [])
      } else {
        toast.error(res.message || 'Không tải được danh sách chỗ dùng')
      }
    } catch (err: any) {
      toast.error(err?.response?.data?.message || 'Không tải được danh sách chỗ dùng')
    } finally {
      setLoadingUsages(false)
    }
  }

  const closeDeleteModal = () => {
    if (actingId != null || removingContentId != null) return
    setDeleting(null)
    setDeleteUsages([])
    setConfirmingHardDelete(false)
  }

  const refreshDeleteUsages = async (videoId: number) => {
    const res = await AdminApi.getVideoUsages(videoId)
    if (res.success) {
      setDeleteUsages(res.data.usages || [])
    }
  }

  const handleRemoveContentUsage = async (usage: AdminVideoUsage) => {
    if (!deleting) return
    if (
      !window.confirm(
        `Xóa nội dung "${usage.contentName}" khỏi khóa "${usage.courseName}"?\n(Nội dung sẽ bị xóa khỏi khóa học, không chỉ gỡ liên kết video.)`
      )
    ) {
      return
    }
    setRemovingContentId(usage.contentId)
    try {
      const res = await AdminApi.deleteCourseContent(usage.contentId)
      if (res.success) {
        toast.success(res.message || 'Đã xóa nội dung')
        await refreshDeleteUsages(deleting.id)
      } else {
        toast.error(res.message || 'Xóa nội dung thất bại')
      }
    } catch (err: any) {
      toast.error(err?.response?.data?.message || 'Xóa nội dung thất bại')
    } finally {
      setRemovingContentId(null)
    }
  }

  const handleConfirmHardDelete = async () => {
    if (!deleting) return
    if (deleteUsages.length > 0) {
      toast.error('Còn nội dung đang gắn video. Hãy xóa từng nội dung trước.')
      return
    }
    if (!confirmingHardDelete) {
      setConfirmingHardDelete(true)
      return
    }
    setActingId(deleting.id)
    try {
      const res = await AdminApi.deleteVideo(deleting.id, { hard: true })
      if (res.success) {
        toast.success(res.message || 'Đã xóa video')
        setDeleting(null)
        setDeleteUsages([])
        setConfirmingHardDelete(false)
        void loadData()
      } else {
        toast.error(res.message || 'Xóa thất bại')
        if (res.data?.usages) {
          setDeleteUsages(res.data.usages)
        } else {
          await refreshDeleteUsages(deleting.id)
        }
        setConfirmingHardDelete(false)
      }
    } catch (err: any) {
      const data = err?.response?.data
      toast.error(data?.message || 'Xóa thất bại')
      if (data?.data?.usages) {
        setDeleteUsages(data.data.usages)
      } else if (deleting) {
        try {
          await refreshDeleteUsages(deleting.id)
        } catch {
          /* ignore */
        }
      }
      setConfirmingHardDelete(false)
    } finally {
      setActingId(null)
    }
  }

  const handleUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0]
    e.target.value = ''
    if (!file) return

    if (file.size > ADMIN_VIDEO_MAX_BYTES) {
      toast.error('File vượt quá giới hạn 1.5GB')
      return
    }

    const controller = new AbortController()
    abortRef.current = controller
    setUploading(true)
    setUploadProgress({
      phase: 'init',
      percent: 0,
      uploadedBytes: 0,
      totalBytes: file.size,
    })

    try {
      const res = await AdminApi.uploadVideoChunked(file, {
        title: uploadTitle.trim() || undefined,
        signal: controller.signal,
        onProgress: setUploadProgress,
      })
      if (res.success && res.data) {
        toast.success(`Đã thêm video: ${res.data.title}`)
        setUploadTitle('')
        setPage(1)
        void loadData({ page: 1 })
      } else {
        toast.error(res.message || 'Upload thất bại')
      }
    } catch (err: any) {
      if (err?.name === 'AbortError' || err?.code === 'ERR_CANCELED') {
        toast.info('Đã hủy upload')
      } else {
        toast.error(err?.response?.data?.message || err?.message || 'Upload thất bại')
      }
    } finally {
      setUploading(false)
      setUploadProgress(null)
      abortRef.current = null
    }
  }

  const cancelUpload = () => {
    abortRef.current?.abort()
  }

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-2xl font-semibold text-m2-on-surface">Thư viện Video</h1>
        <p className="mt-1 text-sm text-m2-on-surface-variant">
          Quản lý video dùng cho bài học (upload tối đa 1.5GB, chia chunk tự động).
        </p>
      </div>

      <div className="rounded-lg border border-m2-outline-variant bg-m2-surface-container p-4 space-y-3">
        <div className="flex flex-col gap-3 sm:flex-row sm:items-end">
          <div className="flex-1">
            <label className="block text-sm font-medium text-m2-on-surface mb-1">
              Tiêu đề khi upload (tuỳ chọn)
            </label>
            <input
              type="text"
              className="w-full rounded-md border border-m2-outline px-3 py-2 text-sm"
              placeholder="Để trống sẽ lấy tên file"
              value={uploadTitle}
              onChange={(e) => setUploadTitle(e.target.value)}
              disabled={uploading}
            />
          </div>
          <div className="flex gap-2">
            <label
              className={`inline-flex cursor-pointer items-center rounded-md px-4 py-2 text-sm font-medium text-white ${
                uploading ? 'bg-m2-outline cursor-not-allowed' : 'bg-m2-primary hover:opacity-90'
              }`}
            >
              <CloudArrowUpIcon className="mr-2 h-5 w-5" />
              {uploading ? 'Đang upload...' : 'Upload video'}
              <input
                ref={fileInputRef}
                type="file"
                accept="video/*,.mp4,.webm,.mov,.mkv"
                className="hidden"
                disabled={uploading}
                onChange={handleUpload}
              />
            </label>
            {uploading && (
              <button
                type="button"
                onClick={cancelUpload}
                className="rounded-md border border-m2-outline px-3 py-2 text-sm text-m2-on-surface hover:bg-m2-surface-high"
              >
                Hủy
              </button>
            )}
          </div>
        </div>
        {uploadProgress && (
          <div>
            <div className="mb-1 flex justify-between text-xs text-m2-on-surface-variant">
              <span>
                {uploadProgress.phase === 'init' && 'Khởi tạo phiên upload...'}
                {uploadProgress.phase === 'uploading' &&
                  `Đang gửi chunk ${(uploadProgress.chunkIndex ?? 0) + 1}/${uploadProgress.totalChunks}`}
                {uploadProgress.phase === 'completing' && 'Đang ghép file & lưu DB...'}
                {uploadProgress.phase === 'done' && 'Hoàn tất'}
              </span>
              <span>
                {uploadProgress.percent}% · {formatBytes(uploadProgress.uploadedBytes)} /{' '}
                {formatBytes(uploadProgress.totalBytes)}
              </span>
            </div>
            <div className="h-2 overflow-hidden rounded-full bg-m2-surface-high">
              <div
                className="h-full bg-m2-primary transition-all duration-200"
                style={{ width: `${uploadProgress.percent}%` }}
              />
            </div>
          </div>
        )}
        <p className="text-xs text-m2-on-surface-variant">
          Định dạng: mp4, webm, mov, mkv. File lớn được chia chunk ~8MB để tránh timeout.
        </p>
      </div>

      <div className="flex flex-col gap-3 sm:flex-row sm:items-center">
        <div className="relative flex-1">
          <MagnifyingGlassIcon className="absolute left-3 top-2.5 h-4 w-4 text-m2-outline" />
          <input
            type="text"
            className="w-full rounded-md border border-m2-outline py-2 pl-9 pr-3 text-sm"
            placeholder="Tìm theo tiêu đề, tên file, hash_id..."
            value={qInput}
            onChange={(e) => setQInput(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === 'Enter') applySearch()
            }}
          />
        </div>
        <button
          type="button"
          onClick={applySearch}
          className="rounded-md bg-m2-surface-high px-4 py-2 text-sm text-m2-on-surface hover:bg-m2-outline-variant"
        >
          Tìm kiếm
        </button>
        <label className="inline-flex items-center gap-2 text-sm text-m2-on-surface-variant">
          <input
            type="checkbox"
            checked={includeInactive}
            onChange={(e) => {
              setIncludeInactive(e.target.checked)
              setPage(1)
            }}
          />
          Hiện cả video đã ẩn
        </label>
      </div>

      <div className="overflow-hidden rounded-lg border border-m2-outline-variant bg-m2-surface-container">
        <div className="overflow-x-auto">
          <table className="min-w-full text-sm">
            <thead className="bg-m2-surface-low text-left text-xs uppercase text-m2-on-surface-variant">
              <tr>
                <th className="px-4 py-3">ID</th>
                <th className="px-4 py-3">Tiêu đề</th>
                <th className="px-4 py-3">File</th>
                <th className="px-4 py-3">Thời lượng</th>
                <th className="px-4 py-3">Ngày tạo</th>
                <th className="px-4 py-3">Trạng thái</th>
                <th className="px-4 py-3 text-right">Thao tác</th>
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr>
                  <td colSpan={7} className="px-4 py-8 text-center text-m2-on-surface-variant">
                    Đang tải...
                  </td>
                </tr>
              ) : items.length === 0 ? (
                <tr>
                  <td colSpan={7} className="px-4 py-8 text-center text-m2-on-surface-variant">
                    Chưa có video nào
                  </td>
                </tr>
              ) : (
                items.map((v) => {
                  const inactive = v.isActive === 0
                  return (
                    <tr key={v.id} className="border-t border-m2-outline-variant">
                      <td className="px-4 py-3 text-m2-on-surface-variant">{v.id}</td>
                      <td className="px-4 py-3">
                        <button
                          type="button"
                          onClick={() => setPreviewing(v)}
                          className="text-left font-medium text-m2-on-surface hover:text-m2-primary hover:underline"
                          title="Xem video"
                        >
                          {v.title}
                        </button>
                        {v.hashId && (
                          <div className="text-xs text-m2-on-surface-variant font-mono">{v.hashId}</div>
                        )}
                      </td>
                      <td className="px-4 py-3 text-m2-on-surface-variant max-w-[200px] truncate">
                        {v.fileName || '—'}
                      </td>
                      <td className="px-4 py-3">{formatDuration(v.duration)}</td>
                      <td className="px-4 py-3 text-m2-on-surface-variant whitespace-nowrap">
                        {v.createdAt ? formatDbDateTimeVi(v.createdAt) : '—'}
                      </td>
                      <td className="px-4 py-3">
                        <span
                          className={`rounded-full px-2 py-0.5 text-xs ${
                            inactive
                              ? 'bg-red-100 text-red-700'
                              : 'bg-m2-secondary/15 text-green-800'
                          }`}
                        >
                          {inactive ? 'Đã ẩn' : 'Active'}
                        </span>
                      </td>
                      <td className="px-4 py-3">
                        <div className="flex justify-end gap-1">
                          <button
                            type="button"
                            title="Xem video"
                            disabled={actingId === v.id}
                            onClick={() => setPreviewing(v)}
                            className="rounded p-1.5 text-m2-on-surface-variant hover:bg-m2-surface-high hover:text-m2-primary"
                          >
                            <PlayCircleIcon className="h-4 w-4" />
                          </button>
                          <button
                            type="button"
                            title="Sửa"
                            disabled={actingId === v.id}
                            onClick={() => openEdit(v)}
                            className="rounded p-1.5 text-m2-on-surface-variant hover:bg-m2-surface-high hover:text-m2-primary"
                          >
                            <PencilSquareIcon className="h-4 w-4" />
                          </button>
                          <button
                            type="button"
                            title={inactive ? 'Hiện lại' : 'Ẩn'}
                            disabled={actingId === v.id}
                            onClick={() => handleToggleActive(v)}
                            className="rounded p-1.5 text-m2-on-surface-variant hover:bg-m2-surface-high"
                          >
                            {inactive ? (
                              <EyeIcon className="h-4 w-4" />
                            ) : (
                              <EyeSlashIcon className="h-4 w-4" />
                            )}
                          </button>
                          <button
                            type="button"
                            title="Xóa video"
                            disabled={actingId === v.id}
                            onClick={() => void openDeleteModal(v)}
                            className="rounded p-1.5 text-m2-on-surface-variant hover:bg-red-50 hover:text-red-600"
                          >
                            <TrashIcon className="h-4 w-4" />
                          </button>
                        </div>
                      </td>
                    </tr>
                  )
                })
              )}
            </tbody>
          </table>
        </div>
        <div className="flex items-center justify-between border-t border-m2-outline-variant px-4 py-3 text-sm text-m2-on-surface-variant">
          <span>
            Tổng {total} video · Trang {page}/{totalPages}
          </span>
          <div className="flex gap-2">
            <button
              type="button"
              disabled={page <= 1}
              onClick={() => setPage((p) => Math.max(1, p - 1))}
              className="rounded border border-m2-outline px-3 py-1 disabled:opacity-40"
            >
              Trước
            </button>
            <button
              type="button"
              disabled={page >= totalPages}
              onClick={() => setPage((p) => p + 1)}
              className="rounded border border-m2-outline px-3 py-1 disabled:opacity-40"
            >
              Sau
            </button>
          </div>
        </div>
      </div>

      {previewing && (
        <AdminVideoPreviewModal video={previewing} onClose={() => setPreviewing(null)} />
      )}

      {editing && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4">
          <div className="w-full max-w-lg rounded-lg bg-m2-surface-container p-5 shadow-xl">
            <h2 className="text-lg font-semibold text-m2-on-surface">Sửa video #{editing.id}</h2>
            <div className="mt-4 space-y-3">
              <div>
                <label className="mb-1 block text-sm font-medium">Tiêu đề</label>
                <input
                  className="w-full rounded-md border border-m2-outline px-3 py-2 text-sm"
                  value={editTitle}
                  onChange={(e) => setEditTitle(e.target.value)}
                />
              </div>
              <div>
                <label className="mb-1 block text-sm font-medium">Mô tả</label>
                <textarea
                  className="w-full rounded-md border border-m2-outline px-3 py-2 text-sm"
                  rows={3}
                  value={editDescription}
                  onChange={(e) => setEditDescription(e.target.value)}
                />
              </div>
              <div>
                <label className="mb-1 block text-sm font-medium">Thời lượng (giây)</label>
                <input
                  type="number"
                  min={0}
                  step={1}
                  className="w-full rounded-md border border-m2-outline px-3 py-2 text-sm"
                  value={editDuration}
                  onChange={(e) => setEditDuration(e.target.value)}
                />
              </div>
              {editing.path && (
                <p className="text-xs text-m2-on-surface-variant break-all">Path: {editing.path}</p>
              )}
            </div>
            <div className="mt-5 flex justify-end gap-2">
              <button
                type="button"
                onClick={() => setEditing(null)}
                className="rounded-md border border-m2-outline px-4 py-2 text-sm"
                disabled={savingEdit}
              >
                Hủy
              </button>
              <button
                type="button"
                onClick={handleSaveEdit}
                disabled={savingEdit}
                className="rounded-md bg-m2-primary px-4 py-2 text-sm font-medium text-white disabled:opacity-50"
              >
                {savingEdit ? 'Đang lưu...' : 'Lưu'}
              </button>
            </div>
          </div>
        </div>
      )}

      {deleting && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4">
          <div className="flex max-h-[90vh] w-full max-w-2xl flex-col rounded-lg bg-m2-surface-container shadow-xl">
            <div className="flex items-start justify-between gap-3 border-b border-m2-outline-variant px-5 py-4">
              <div className="min-w-0">
                <h2 className="text-lg font-semibold text-m2-on-surface">Xóa video</h2>
                <p className="mt-1 truncate text-sm text-m2-on-surface-variant">
                  #{deleting.id} · {deleting.title}
                </p>
              </div>
              <button
                type="button"
                title="Đóng"
                onClick={closeDeleteModal}
                disabled={actingId != null || removingContentId != null}
                className="rounded p-1.5 text-m2-on-surface-variant hover:bg-m2-surface-high hover:text-m2-on-surface disabled:opacity-40"
              >
                <XMarkIcon className="h-5 w-5" />
              </button>
            </div>

            <div className="flex-1 overflow-y-auto px-5 py-4">
              {loadingUsages ? (
                <p className="py-8 text-center text-sm text-m2-on-surface-variant">
                  Đang kiểm tra chỗ dùng...
                </p>
              ) : deleteUsages.length > 0 ? (
                <div className="space-y-3">
                  <p className="text-sm text-m2-on-surface">
                    Video đang được dùng trong{' '}
                    <span className="font-semibold">{deleteUsages.length}</span> nội dung.
                    Hãy xóa từng nội dung bên dưới trước khi xóa video.
                  </p>
                  <ul className="divide-y divide-m2-outline-variant rounded-md border border-m2-outline-variant">
                    {deleteUsages.map((u) => (
                      <li
                        key={u.contentId}
                        className="flex items-start justify-between gap-3 px-3 py-3"
                      >
                        <div className="min-w-0">
                          <div className="text-sm font-medium text-m2-on-surface">
                            {u.contentName}
                          </div>
                          <div className="mt-0.5 text-xs text-m2-on-surface-variant">
                            Khóa: {u.courseName}
                            <span className="text-m2-outline"> · </span>
                            Nội dung #{u.contentId}
                          </div>
                        </div>
                        <button
                          type="button"
                          disabled={removingContentId != null || actingId != null}
                          onClick={() => void handleRemoveContentUsage(u)}
                          className="shrink-0 rounded-md border border-red-200 px-2.5 py-1.5 text-xs font-medium text-red-700 hover:bg-red-50 disabled:opacity-40"
                        >
                          {removingContentId === u.contentId ? 'Đang xóa...' : 'Xóa nội dung'}
                        </button>
                      </li>
                    ))}
                  </ul>
                </div>
              ) : (
                <div className="space-y-2 py-2">
                  <p className="text-sm text-m2-on-surface">
                    Không còn nội dung nào gắn với video này. Bạn có thể xóa video khỏi hệ thống.
                  </p>
                  <p className="text-xs text-red-700">
                    Xóa sẽ gỡ bản ghi và file trên đĩa — không hoàn tác được.
                  </p>
                  {confirmingHardDelete && (
                    <p className="rounded-md bg-red-50 px-3 py-2 text-sm text-red-800">
                      Nhấn lại &quot;Xác nhận xóa video&quot; để tiếp tục.
                    </p>
                  )}
                </div>
              )}
            </div>

            <div className="flex justify-end gap-2 border-t border-m2-outline-variant px-5 py-4">
              <button
                type="button"
                onClick={closeDeleteModal}
                disabled={actingId != null || removingContentId != null}
                className="rounded-md border border-m2-outline px-4 py-2 text-sm disabled:opacity-40"
              >
                Đóng
              </button>
              <button
                type="button"
                disabled={
                  loadingUsages ||
                  deleteUsages.length > 0 ||
                  actingId != null ||
                  removingContentId != null
                }
                onClick={() => void handleConfirmHardDelete()}
                className="rounded-md bg-red-600 px-4 py-2 text-sm font-medium text-white hover:bg-red-700 disabled:cursor-not-allowed disabled:opacity-40"
              >
                {actingId === deleting.id
                  ? 'Đang xóa...'
                  : confirmingHardDelete
                    ? 'Xác nhận xóa video'
                    : 'Xóa video'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}

export default AdminVideosPage
